import { type ChangeEvent, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import type { TextStyleValue } from '@elfsight/configurator-contracts';
import {
  FormBuilderThumbScaleDislike,
  FormBuilderThumbScaleLike
} from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { textStyleMixin } from '@elfsight/widget-ui';
import {
  createReadableClass,
  getInvertedColor,
  isTransparentColor,
  roundTo4
} from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';
import { LINE_HEIGHT_RATIO } from '../constants';
import type { CommonFieldProps } from '../types';

const LIKE_COLOR = '#0EC12F';
const DISLIKE_COLOR = '#EF4623';

export interface ThumbScaleProps extends CommonFieldProps {
  likeText: string;
  dislikeText: string;
}

export function ThumbScale({
  label,
  likeText,
  dislikeText,
  onChange,
  onFocus,
  value,
  required,
  fieldColor = commonFieldsSettings.fieldColor,
  fieldFillColor = commonFieldsSettings.fieldFillColor,
  fieldBorderType = commonFieldsSettings.fieldBorderType,
  fieldBorderWidth = commonFieldsSettings.fieldBorderWidth,
  fieldBorderColor = commonFieldsSettings.fieldBorderColor,
  fieldBorderRadius = commonFieldsSettings.fieldBorderRadius,
  fieldFontStyle = commonFieldsSettings.fieldFontStyle
}: ThumbScaleProps) {
  const [checkedIndex, setCheckedIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(0);

  const withUnderline = fieldBorderType === 'underline';
  const isTransparentFillColor = isTransparentColor(fieldFillColor);
  const borderColor = fieldBorderType ? fieldBorderColor : 'transparent';
  const lineHeight = roundTo4(fieldFontStyle.fontSize * LINE_HEIGHT_RATIO);

  const thumbProps = {
    onMouseLeave: () => handleMouseOver(0),
    _hoverIndex: hoverIndex,
    _checkedIndex: checkedIndex,
    _withUnderline: withUnderline,
    _borderWidth: fieldBorderWidth,
    _borderRadius: fieldBorderRadius,
    _borderColor: borderColor,
    _fieldColor: fieldColor,
    _fillColor: fieldFillColor,
    _textStyle: fieldFontStyle,
    _lineHeight: lineHeight,
    _isTransparentFillColor: isTransparentFillColor
  };

  const handleMouseOver = (i: number) => setHoverIndex(i);
  const handleCheck = (event: ChangeEvent<HTMLInputElement>, i: number) => {
    const { value } = event.target;
    onChange?.(value, event);
    setCheckedIndex(i);
  };

  useEffect(() => {
    if (value) {
      const derivedCheckIndex = value === (likeText || 'Like') ? 1 : 2;
      setCheckedIndex(derivedCheckIndex);
    }
  }, [likeText, value]);

  return (
    <ThumbsContainer role="radiogroup" aria-required={required}>
      <Thumb
        onMouseEnter={() => handleMouseOver(1)}
        _activeColor={LIKE_COLOR}
        _index={1}
        {...thumbProps}
      >
        <Input
          type="radio"
          name={label}
          value={likeText || 'Like'}
          onChange={(e) => handleCheck(e, 1)}
          onFocus={onFocus}
        />
        <StyledIcon
          component={FormBuilderThumbScaleLike}
          size={lineHeight}
          _isCaption={!!likeText}
        />
        {likeText}
      </Thumb>
      <Thumb
        onMouseEnter={() => handleMouseOver(2)}
        _activeColor={DISLIKE_COLOR}
        _index={2}
        {...thumbProps}
      >
        <Input
          type="radio"
          name={label}
          value={dislikeText || 'Dislike'}
          onChange={(e) => handleCheck(e, 2)}
          onFocus={onFocus}
        />
        <StyledIcon
          component={FormBuilderThumbScaleDislike}
          size={lineHeight}
          _isCaption={!!dislikeText}
        />
        {dislikeText}
      </Thumb>
    </ThumbsContainer>
  );
}

interface ThumbProps {
  _activeColor: string;
  _index: number;
  _hoverIndex: number;
  _checkedIndex: number;
  _withUnderline: boolean;
  _borderWidth: number;
  _borderRadius: number;
  _borderColor: string;
  _fieldColor: string;
  _fillColor: string;
  _textStyle: TextStyleValue;
  _lineHeight: number;
  _isTransparentFillColor: boolean;
}

const Thumb = styled.label.attrs<ThumbProps>(({ _checkedIndex, _index }) => ({
  className: createReadableClass(
    {
      component: 'fields',
      element: 'thumb-scale-thumb',
      modifier: _index === 1 ? 'like' : 'dislike',
      index: _checkedIndex === _index && 'checked'
    },
    [createReadableClass({ component: 'fields', element: 'thumb-scale-thumb' })]
  )
}))`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  transition: 0.1s all ease;
  &:first-child {
    margin-right: 12px;
  }
  ${textStyleMixin};
  padding: ${({ _textStyle }) =>
    `${_textStyle.fontSize / 2}px ${_textStyle.fontSize}px`};
  line-height: ${({ _lineHeight }) => _lineHeight}px;
  color: ${({
    _isTransparentFillColor,
    _activeColor,
    _fieldColor,
    _index,
    _hoverIndex,
    _checkedIndex
  }) =>
    _index === _hoverIndex || _index === _checkedIndex
      ? _isTransparentFillColor
        ? _activeColor
        : getInvertedColor(_activeColor)
      : _fieldColor};
  fill: ${({
    _isTransparentFillColor,
    _activeColor,
    _fieldColor,
    _index,
    _hoverIndex,
    _checkedIndex
  }) =>
    _index === _hoverIndex || _index === _checkedIndex
      ? _isTransparentFillColor
        ? _activeColor
        : getInvertedColor(_activeColor)
      : _fieldColor};
  background: ${({
    _isTransparentFillColor,
    _activeColor,
    _fillColor,
    _index,
    _hoverIndex,
    _checkedIndex
  }) =>
    !_isTransparentFillColor &&
    (_index === _hoverIndex || _index === _checkedIndex)
      ? _activeColor
      : _fillColor};
  ${({ _withUnderline, _borderWidth }) =>
    _withUnderline
      ? css`
          border: none;
          border-bottom: ${_borderWidth}px solid;
        `
      : css`
          border: ${_borderWidth}px solid;
        `}
  border-color: ${({
    _index,
    _hoverIndex,
    _checkedIndex,
    _borderColor,
    _activeColor
  }) =>
    _index === _hoverIndex || _index === _checkedIndex
      ? _activeColor
      : _borderColor};
  border-radius: ${({ _borderRadius }) => _borderRadius}px;
`;

const ThumbsContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'thumb-scale-container'
  })
})`
  display: flex;
  height: max-content;
`;

interface StyledIconProps {
  _isCaption: boolean;
}

const StyledIcon = styled(Icon).attrs<StyledIconProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'thumb-scale-icon'
  })
})`
  margin-right: ${({ _isCaption }) => (_isCaption ? 4 : 0)}px;
`;

const Input = styled.input.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'thumb-scale-input'
  })
})`
  position: absolute;
  cursor: pointer;
  margin: 0;
  opacity: 0;
  height: 100%;
  width: 100%;
  bottom: 0;
  right: 0;
`;
